import {
    Box,
    Flex,
    Text,
    Select,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Input,
    useToast
  } from "@chakra-ui/react";
  import { MdArrowUpward, MdArrowDownward } from "react-icons/md";
  import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
  import axios from "axios";
  import { useEffect, useState } from "react";
  import Swal from "sweetalert2";
  import { showSuccessToast, showErrorToast } from "util/alertUtil";
  import { EDUCATION_LEVEL_OPTIONS } from "variables/constant";
  
  function ChapterList() {
  
    const toast = useToast();
    const API = process.env.REACT_APP_API_URL; // ✅ ให้ใช้ตัวเดียวกันทั้งระบบ
  
    const [classCode, setClassCode] = useState("");
    const [subjectId, setSubjectId] = useState("");
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(false);
  
    const [chapterName, setChapterName] = useState("");
    const [editingChapter, setEditingChapter] = useState(null);
  
    const { isOpen, onOpen, onClose } = useDisclosure();
  
    /* =======================
        Fetch Subject
    ========================*/
    const fetchSubjects = async () => {
      try {
        const res = await axios.post(
          `${API}/subjects/list_all_subjects`,
          {},
          { withCredentials: true }
        );
  
        if (res.data?.status === "success") {
          setSubjects(res.data.subjects || []);
        }
      } catch (err) {
        showErrorToast(toast, "ผิดพลาด", "โหลดวิชาไม่สำเร็จ");
      }
    };
  
    /* =======================
        Fetch Chapters
    ========================*/
    const fetchChapters = async () => {
      if (!classCode || !subjectId) return;
  
      try {
        setLoading(true);
  
        const res = await axios.post(
          `${API}/chapter/list_chapter_titles`,
          {
            class_code: classCode,
            subject_id: subjectId
          },
          { withCredentials: true }
        );
  
        if (res.data?.status === "success") {
          setChapters(res.data.result || []);
        } else {
          setChapters([]);
        }
  
      } catch (err) {
        console.log(err);
        showErrorToast(toast, "ผิดพลาด", "โหลดบทเรียนไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchSubjects();
    }, []);
  
    useEffect(() => {
      fetchChapters();
    }, [classCode, subjectId]);
  
    /* =======================
        Save Chapter
    ========================*/
    const handleSave = async () => {
  
      if (!classCode || !subjectId) {
        Swal.fire("กรุณาเลือกชั้นเรียนและวิชา");
        return;
      }
  
      if (!chapterName.trim()) {
        Swal.fire("กรุณากรอกชื่อบท");
        return;
      }
  
      try {
        const res = await axios.post(
          `${API}/chapter/chapter_title_save`,
          {
            opt: editingChapter ? "update" : "insert",
            id: editingChapter?.id,
            chapter_name: chapterName,
            class_code: classCode,
            subject_id: subjectId
          },
          { withCredentials: true }
        );
  
        if (res.data?.status === "success") {
          showSuccessToast(toast, "สำเร็จ", "บันทึกเรียบร้อย");
          fetchChapters();
          setChapterName("");
          setEditingChapter(null);
          onClose();
        }
  
      } catch (err) {
        showErrorToast(toast, "ผิดพลาด", "บันทึกไม่สำเร็จ");
      }
    };
  
    /* =======================
        Delete
    ========================*/
    const handleDelete = async (id) => {
  
      const result = await Swal.fire({
        title: "ยืนยันลบ?",
        icon: "warning",
        showCancelButton: true
      });
  
      if (!result.isConfirmed) return;
  
      try {
        await axios.post(
          `${API}/chapter/delete_chapter_title`,
          { id },
          { withCredentials: true }
        );
  
        fetchChapters();
        showSuccessToast(toast, "สำเร็จ", "ลบเรียบร้อย");
  
      } catch (err) {
        showErrorToast(toast, "ผิดพลาด", "ลบไม่สำเร็จ");
      }
    };
  
    /* =======================
        Reorder
    ========================*/
    const moveUp = async (index) => {
      if (index === 0) return;
  
      const current = chapters[index];
      const prev = chapters[index - 1];
  
      await axios.post(
        `${API}/chapter/swap_chapter_order`,
        {
          chapter_id1: current.id,
          chapter_id2: prev.id
        },
        { withCredentials: true }
      );
  
      fetchChapters();
    };
  
    const moveDown = async (index) => {
      if (index === chapters.length - 1) return;
  
      const current = chapters[index];
      const next = chapters[index + 1];
  
      await axios.post(
        `${API}/chapter/swap_chapter_order`,
        {
          chapter_id1: current.id,
          chapter_id2: next.id
        },
        { withCredentials: true }
      );
  
      fetchChapters();
    };
  
    return (
      <Flex direction="column" pt={{ sm: "125px", lg: "100px" }}>
        <Flex w="100%" direction="column" p={4}>
  
          <Flex mb={4} gap={3} wrap="wrap">
  
            <Select
              placeholder="เลือกชั้นเรียน"
              value={classCode}
              onChange={(e) => setClassCode(e.target.value)}
              maxW="200px"
            >
              {EDUCATION_LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
  
            <Select
              placeholder="เลือกวิชา"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              maxW="250px"
            >
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.subjectThai}
                </option>
              ))}
            </Select>
  
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              isDisabled={!classCode || !subjectId}
              onClick={() => {
                setEditingChapter(null);
                setChapterName("");
                onOpen();
              }}
            >
              เพิ่มบท
            </Button>
          </Flex>
  
          <Table variant="striped">
            <Thead>
              <Tr>
                <Th>ลำดับ</Th>
                <Th>ชื่อบท</Th>
                <Th>จัดการ</Th>
              </Tr>
            </Thead>
            <Tbody>
              {chapters.map((c, index) => (
                <Tr key={c.id}>
                  <Td>{index + 1}</Td>
                  <Td>{c.chapter_name}</Td>
                  <Td>
                    <IconButton
                      icon={<MdArrowUpward />}
                      size="sm"
                      mr={2}
                      isDisabled={index === 0}
                      onClick={() => moveUp(index)}
                    />
                    <IconButton
                      icon={<MdArrowDownward />}
                      size="sm"
                      mr={2}
                      isDisabled={index === chapters.length - 1}
                      onClick={() => moveDown(index)}
                    />
                    <IconButton
                      icon={<EditIcon />}
                      size="sm"
                      mr={2}
                      onClick={() => {
                        setEditingChapter(c);
                        setChapterName(c.chapter_name);
                        onOpen();
                      }}
                    />
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDelete(c.id)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
  
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                {editingChapter ? "แก้ไขบท" : "เพิ่มบทใหม่"}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Input
                  placeholder="ชื่อบท"
                  value={chapterName}
                  onChange={(e) => setChapterName(e.target.value)}
                />
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" mr={3} onClick={handleSave}>
                  บันทึก
                </Button>
                <Button variant="ghost" onClick={onClose}>
                  ยกเลิก
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
  
        </Flex>
      </Flex>
    );
  }
  
  export default ChapterList;